import { useState } from "react"
import { useTranslation } from "react-i18next"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { DatePicker, VISUAL_DATE_FORMAT } from "@/components/ui/date-picker"
import { DropdownSelect } from "@/components/ui/dropdown-select"
import { DateOnly } from "@/lib/date-only"
import { BetterDocInsuranceCountrySelect } from "@/modules/admin/better-doc/components/better-doc-insurance-country-select"
import { BetterDocRecommendationQueryTable } from "@/modules/admin/better-doc/components/better-doc-recommendation-query-table"
import { BetterDocRemoteRequestStatus } from "@/modules/admin/better-doc/components/better-doc-remote-request-status"
import {
  BETTER_DOC_RECOMMENDATION_STATE_GROUPS,
  BETTER_DOC_STATES_BY_STATE_GROUP,
  DEFAULT_INSURANCE_COUNTRY,
  DEFAULT_RECOMMENDATION_STATE_GROUP,
  DEFAULT_UPDATED_AFTER_DATE,
} from "@/modules/admin/better-doc/constants/better-doc-constants"
import { useBetterDocFetch } from "@/modules/admin/better-doc/hooks/useBetterDocFetch"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { findEnumValue } from "@/modules/admin/better-doc/lib/find-enum-value"
import { fetchBetterDocRecommendations } from "@/modules/admin/better-doc/services/betterDocRemoteRecommendationsQuery"
import { Country, de_BetterDocRecommendationStateGroup } from "@/types/__generated__/union-types"

type RecommendationQueryRequest = {
  insuranceCountry: Country
  stateGroup: de_BetterDocRecommendationStateGroup
  updatedAfter: Date
  attempt: number
}

const fetchRecommendationsForRequest = ({ insuranceCountry, stateGroup, updatedAfter }: RecommendationQueryRequest) =>
  fetchBetterDocRecommendations(insuranceCountry, stateGroup, new DateOnly(updatedAfter).toString())

export const BetterDocRecommendationQuery = () => {
  const { t } = useTranslation()
  const [queryRequest, setQueryRequest] = useState<RecommendationQueryRequest>({
    insuranceCountry: DEFAULT_INSURANCE_COUNTRY,
    stateGroup: DEFAULT_RECOMMENDATION_STATE_GROUP,
    updatedAfter: DEFAULT_UPDATED_AFTER_DATE,
    attempt: 0,
  })
  const queryState = useBetterDocFetch(queryRequest, fetchRecommendationsForRequest)

  const stateGroupOptions = BETTER_DOC_RECOMMENDATION_STATE_GROUPS.map((stateGroup) => ({
    value: stateGroup,
    label: getBetterDocEnumLabel(t, "recommendationStateGroup", stateGroup),
  }))

  const stateGroupStatesText = BETTER_DOC_STATES_BY_STATE_GROUP[queryRequest.stateGroup]
    .map((state) => getBetterDocEnumLabel(t, "recommendationState", state))
    .join(", ")

  const handleInsuranceCountryChange = (insuranceCountry: Country) => {
    setQueryRequest((currentRequest) => ({
      ...currentRequest,
      insuranceCountry,
      attempt: currentRequest.attempt + 1,
    }))
  }

  const handleStateGroupSelect = (selectedValue: string | null | undefined) => {
    const stateGroup = findEnumValue(BETTER_DOC_RECOMMENDATION_STATE_GROUPS, selectedValue)
    if (stateGroup !== null) {
      setQueryRequest((currentRequest) => ({ ...currentRequest, stateGroup, attempt: currentRequest.attempt + 1 }))
    }
  }

  const handleUpdatedAfterSelect = (updatedAfter: Date) => {
    setQueryRequest((currentRequest) => ({ ...currentRequest, updatedAfter, attempt: currentRequest.attempt + 1 }))
  }

  const handleLoad = () => {
    setQueryRequest((currentRequest) => ({ ...currentRequest, attempt: currentRequest.attempt + 1 }))
  }

  const renderQueryState = () => {
    switch (queryState.status) {
      case "idle":
      case "loading":
        return <LoadingSpinner size={24} className="flex justify-center p-8" />
      case "failed":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-recommendation-query-error">
            {t("admin.betterDoc.requestFailed")}
          </Banner>
        )
      case "loaded":
        return (
          <div className="flex flex-col gap-4">
            <BetterDocRemoteRequestStatus
              status={queryState.data.status}
              httpStatusCode={queryState.data.httpStatusCode}
              errorReason={queryState.data.errorReason}
              exchangeId={queryState.data.exchangeId}
            />
            {queryState.data.hasMoreRecommendations ? (
              <Banner type="inline" variant="warning" data-testid="better-doc-admin-recommendation-query-has-more">
                {t("admin.betterDoc.recommendationQuery.hasMoreRecommendations")}
              </Banner>
            ) : null}
            {queryState.data.status === "RECEIVED" ? (
              <BetterDocRecommendationQueryTable recommendations={queryState.data.recommendations} />
            ) : null}
          </div>
        )
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="better-doc-admin-recommendation-query">
      <div className="flex flex-wrap items-start gap-4">
        <BetterDocInsuranceCountrySelect
          insuranceCountry={queryRequest.insuranceCountry}
          onInsuranceCountryChange={handleInsuranceCountryChange}
          testId="better-doc-admin-recommendation-query-insurance-country"
        />
        <div className="flex flex-col gap-1">
          <DropdownSelect
            label={t("admin.betterDoc.recommendationQuery.stateGroupLabel")}
            options={stateGroupOptions}
            defaultValue={queryRequest.stateGroup}
            onSelect={handleStateGroupSelect}
            className="w-56"
            testId="better-doc-admin-recommendation-query-state-group"
          />
          <span
            className="text-on-surface-subdued max-w-56 text-xs"
            data-testid="better-doc-admin-recommendation-query-state-group-states">
            {t("admin.betterDoc.recommendationQuery.stateGroupStates", { states: stateGroupStatesText })}
          </span>
        </div>
        <DatePicker
          label={t("admin.betterDoc.recommendationQuery.updatedAfter")}
          dataTestId="better-doc-admin-recommendation-query-updated-after"
          readOnly={false}
          placeholder={VISUAL_DATE_FORMAT}
          value={queryRequest.updatedAfter}
          enableReset={false}
          onSelect={handleUpdatedAfterSelect}
          onReset={() => undefined}
        />
        <div className="flex h-12 items-center">
          <Button
            type="button"
            variant="secondary"
            disabled={queryState.status === "loading"}
            onClick={handleLoad}
            data-testid="better-doc-admin-recommendation-query-load-button">
            {t("admin.betterDoc.recommendationQuery.load")}
          </Button>
        </div>
      </div>
      <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-recommendation-query-hint">
        {t("admin.betterDoc.recommendationQuery.hint")}
      </p>
      {renderQueryState()}
    </div>
  )
}
